import { useQuery } from '@tanstack/react-query';

import { baPerfectOrderApi } from 'api/FBM/baPerfectOrder';

export const baPerfectOrderQueryKeys = {
    all: ['baPerfectOrder'],
    report: (range = {}) => ['baPerfectOrder', 'report', range.start ?? null, range.end ?? null],
};

/**
 * React Query hook for the Blue Apron Perfect Order Report.
 *
 * Using React Query (lettuce's client fetch/cache layer) gives us request
 * deduplication and caching across mounts for free. staleTime matches the
 * backend's 15-minute cache TTL so we don't re-hit the endpoint more often than
 * its data can change. The AbortSignal is forwarded so unmounts cancel in-flight
 * requests. On a 403 we surface a typed error so the page can distinguish
 * "not authorized" from a transient failure.
 */
export const useBAPerfectOrderReport = ({ end, start } = {}, options = {}) =>
    useQuery({
        queryKey: baPerfectOrderQueryKeys.report({ end, start }),
        queryFn: async ({ signal }) => {
            const response = await baPerfectOrderApi.getPerfectOrderReport({ end, signal, start });

            if (response.statusCode === 403) {
                throw Object.assign(new Error('Forbidden'), { forbidden: true });
            }
            if (!response.status) {
                throw new Error(response.msg ?? 'Failed to load the Perfect Order Report');
            }

            return response.data?.rows ?? [];
        },
        staleTime: 15 * 60 * 1000,
        ...options,
    });
